import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { flushSync } from 'react-dom';
import { Flip } from 'gsap/Flip';
import type { Expertise } from '../../content/site';
import { gsap, useGSAP, EASE, MOTION_OK } from '../../lib/gsap';
import { prefersReducedMotion } from '../../lib/useReducedMotion';
import { EXPERTISE_ICONS, surfaceAt } from './surfaces';
import { ExpertiseFacts, ExpertiseSummary } from './ExpertiseDetails';

gsap.registerPlugin(Flip);

/** Width of a collapsed slice: about 11% of the row at every desktop width. */
const RAIL = 'clamp(6rem, 10.5vw, 9.5rem)';
/** Matches rounded-card. */
const RADIUS = 20;
/** Hover intent: the pointer must rest briefly before a slice opens. */
const HOVER_DELAY = 90;

/**
 * Flip animates the slice surfaces with scaleX (transform only). A scaled box would
 * stretch its corners (and thicken a 1px border), so each frame the radius and the
 * hairline are divided by the current scale: corners keep reading as 20px, the border as
 * a hairline (down to the device pixel).
 */
function keepCorners(el: HTMLElement) {
	const sx = Number(gsap.getProperty(el, 'scaleX')) || 1;
	const sy = Number(gsap.getProperty(el, 'scaleY')) || 1;
	el.style.borderRadius = `${RADIUS / sx}px / ${RADIUS / sy}px`;
	if (el.dataset.hairline !== undefined) {
		el.style.borderLeftWidth = el.style.borderRightWidth = `${Math.max(1 / sx, 0.34)}px`;
		el.style.borderTopWidth = el.style.borderBottomWidth = `${Math.max(1 / sy, 0.34)}px`;
	}
}

function releaseCorners(el: HTMLElement) {
	el.style.removeProperty('border-radius');
	el.style.removeProperty('border-width');
}

const ids = (item: Expertise) => ({ button: `expertise-${item.id}`, panel: `expertise-${item.id}-detail` });

/**
 * Desktop (>= 1024px) "Horizontal Accordion": four slices in one row, exactly one open.
 * Opens on hover (with intent), focus, click, Enter/Space; ArrowLeft/ArrowRight/Home/End
 * move between slices. Each slice header is a real <button> in an <h3>, with
 * aria-expanded and aria-controls pointing at its detail region.
 *
 * Motion: GSAP Flip records the surfaces, icons and rails, the grid switches columns,
 * then Flip plays the change back with transforms only (surfaces scale, labels slide).
 * The opened slice's content fades and slides in once the resize has mostly happened.
 * Reduced motion: the layout switches instantly.
 */
export function HorizontalAccordion({ items }: { items: Expertise[] }) {
	const listRef = useRef<HTMLUListElement>(null);
	const buttons = useRef<(HTMLButtonElement | null)[]>([]);
	const [active, setActive] = useState(0);
	const activeRef = useRef(0);
	const hoverTimer = useRef<number>();

	const { contextSafe } = useGSAP(
		() => {
			const mm = gsap.matchMedia();
			mm.add(MOTION_OK, () => {
				gsap.from('[data-slice]', {
					y: 30,
					opacity: 0,
					duration: 1.1,
					ease: EASE,
					stagger: 0.08,
					scrollTrigger: { trigger: listRef.current, start: 'top 82%', once: true },
				});
			});
		},
		{ scope: listRef },
	);

	useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

	const activate = contextSafe((index: number) => {
		const list = listRef.current;
		if (!list || index === activeRef.current) return;

		const animate = !prefersReducedMotion();
		const state = animate ? Flip.getState(list.querySelectorAll('[data-flip]')) : null;

		activeRef.current = index;
		flushSync(() => setActive(index));
		if (!state) return;

		const surfaces = gsap.utils.toArray<HTMLElement>('[data-flip="surface"]', list);
		const settle = () => surfaces.forEach(releaseCorners);
		Flip.from(state, {
			duration: 0.85,
			ease: EASE,
			scale: true,
			onUpdate: () => surfaces.forEach(keepCorners),
			onComplete: settle,
			onInterrupt: settle,
		});
		surfaces.forEach(keepCorners);

		// The content waits until the surface has covered most of its new width, then
		// rises into place, so text never hangs outside the growing slice.
		const panel = list.querySelector<HTMLElement>(`[data-panel="${index}"]`);
		if (panel) {
			gsap.fromTo(
				panel.querySelectorAll('[data-reveal]'),
				{ opacity: 0, y: 16 },
				{ opacity: 1, y: 0, duration: 0.8, ease: EASE, stagger: 0.045, delay: 0.28, overwrite: true },
			);
		}
	});

	const onPointerEnter = (index: number) => {
		window.clearTimeout(hoverTimer.current);
		hoverTimer.current = window.setTimeout(() => activate(index), HOVER_DELAY);
	};
	const onPointerLeave = () => window.clearTimeout(hoverTimer.current);

	const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
		const count = items.length;
		let target: number;
		switch (event.key) {
			case 'ArrowRight':
				target = (index + 1) % count;
				break;
			case 'ArrowLeft':
				target = (index - 1 + count) % count;
				break;
			case 'Home':
				target = 0;
				break;
			case 'End':
				target = count - 1;
				break;
			default:
				return;
		}
		event.preventDefault();
		activate(target);
		buttons.current[target]?.focus();
	};

	const columns = items.map((_, i) => (i === active ? 'minmax(0, 1fr)' : RAIL)).join(' ');

	return (
		<ul
			ref={listRef}
			className="grid h-[clamp(34rem,72vh,40rem)] gap-3"
			style={{ gridTemplateColumns: columns }}
		>
			{items.map((item, index) => {
				const isActive = index === active;
				const surface = surfaceAt(index);
				const SliceIcon = EXPERTISE_ICONS[item.icon];
				const { button, panel } = ids(item);

				return (
					<li key={item.id} data-slice className="relative min-w-0">
						<div
							data-flip="surface"
							data-hairline={surface.hairline ? '' : undefined}
							aria-hidden
							className={`absolute inset-0 rounded-card ${surface.bg}`}
						/>
						<div
							data-flip="icon"
							aria-hidden
							className={`pointer-events-none absolute left-7 top-7 xl:left-9 xl:top-9 ${surface.mark}`}
						>
							<SliceIcon size={34} weight="light" />
						</div>
						<div
							data-flip="rail"
							aria-hidden
							className={`pointer-events-none absolute bottom-7 left-7 transition-opacity ease-expo motion-reduce:delay-0 xl:bottom-9 xl:left-9 ${
								isActive ? 'opacity-0 duration-150' : 'opacity-100 delay-300 duration-500'
							} ${surface.ink}`}
						>
							<span className="block rotate-180 whitespace-nowrap font-display text-[1.625rem] font-bold leading-none tracking-[-0.02em] [writing-mode:vertical-rl]">
								{item.title}
							</span>
						</div>

						<h3>
							<button
								ref={(el) => {
									buttons.current[index] = el;
								}}
								type="button"
								id={button}
								aria-expanded={isActive}
								aria-controls={panel}
								onMouseEnter={() => onPointerEnter(index)}
								onMouseLeave={onPointerLeave}
								onFocus={() => activate(index)}
								onClick={() => activate(index)}
								onKeyDown={(event) => onKeyDown(event, index)}
								className={`absolute inset-0 z-10 rounded-card ${
									isActive ? 'cursor-default' : 'cursor-pointer'
								} ${surface.focus}`}
							>
								<span className="sr-only">{item.title}</span>
							</button>
						</h3>

						<div className={`relative h-full p-7 xl:p-9 ${surface.ink}`}>
							<div
								id={panel}
								role="region"
								aria-labelledby={button}
								data-panel={index}
								hidden={!isActive}
								className="h-full"
							>
								{/* Layout lives on an inner wrapper: a display utility on the panel
								    itself would override the hidden attribute. */}
								<div className="flex h-full flex-col">
									<p
										data-reveal
										aria-hidden
										className="mt-[4.25rem] font-display text-[clamp(1.875rem,2.7vw,2.5rem)] font-bold leading-none tracking-[-0.03em] xl:mt-20"
									>
										{item.title}
									</p>
									<div className="mt-auto grid gap-6 pt-8 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] xl:gap-12">
										<ExpertiseSummary item={item} surface={surface} />
										<ExpertiseFacts item={item} surface={surface} />
									</div>
								</div>
							</div>
						</div>
					</li>
				);
			})}
		</ul>
	);
}
